import * as React from "react";
import { useCallback, useMemo } from "react";
import styled, { css, useTheme } from "styled-components";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { layoutToPositions, positionsToLayout } from "views/logic/widgets/normalizeWidgetPositions";
import { widgetDragHandleClass } from "views/components/widgets/Constants";
const WidthAdjustedReactGridLayout = WidthProvider(Responsive);
const WidthProvidedGridLayout = ({
  width,
  ...props
}) => width ? /* @__PURE__ */ React.createElement(Responsive, { width, ...props }) : /* @__PURE__ */ React.createElement(WidthAdjustedReactGridLayout, { width, ...props });
const StyledWidthProvidedGridLayout = styled(WidthProvidedGridLayout)(
  ({ theme }) => css`
    &.locked {
      .${widgetDragHandleClass} {
        display: none;
      }
    }

    &.unlocked {
      .react-draggable {
        cursor: move;
      }
    }

    .react-grid-item.react-grid-placeholder {
      background: ${theme.colors.variant.info};
    }

    .actions {
      cursor: default;
    }
  `
);
const COLUMN_WIDTH = 175;
const ROW_HEIGHT = 100;
const COLUMNS = {
  xxl: 12,
  xl: 10,
  lg: 8,
  md: 6,
  sm: 4,
  xs: 2
};
const BREAKPOINTS = {
  xxl: COLUMN_WIDTH * COLUMNS.xxl,
  xl: COLUMN_WIDTH * COLUMNS.xl,
  lg: COLUMN_WIDTH * COLUMNS.lg,
  md: COLUMN_WIDTH * COLUMNS.md,
  sm: COLUMN_WIDTH * COLUMNS.sm,
  xs: COLUMN_WIDTH * COLUMNS.xs
};
const _gridClass = (locked, isResizable, draggableHandle, propsClassName) => {
  const className = `${propsClassName}`;
  if (locked || !isResizable) {
    return `${className} locked`;
  }
  if (draggableHandle) {
    return className;
  }
  return `${className} unlocked`;
};
const _onLayoutChange = (newLayout, callback) => {
  if (typeof callback !== "function") {
    return void 0;
  }
  const newPositions = layoutToPositions(newLayout.filter(({ i }) => !i.startsWith("gap")));
  return callback(newPositions);
};
const removeGaps = (_layout) => {
  const gapIndices = [];
  _layout.forEach((item, idx) => {
    if (item.i.startsWith("gap")) {
      gapIndices.push(idx);
    }
  });
  gapIndices.reverse().forEach((idx) => _layout.splice(idx, 1));
};
const ReactGridContainer = ({
  children,
  className = void 0,
  columns = COLUMNS,
  draggableHandle = void 0,
  isResizable = true,
  locked = false,
  measureBeforeMount = false,
  onPositionsChange,
  onSyncLayout: _onSyncLayout = void 0,
  positions,
  rowHeight = ROW_HEIGHT,
  width = void 0
}) => {
  const theme = useTheme();
  const cellMargin = theme.spacings.px.xs;
  const onLayoutChange = useCallback(
    (layout2) => _onLayoutChange(layout2, onPositionsChange),
    [onPositionsChange]
  );
  const onSyncLayout = useCallback((layout2) => _onLayoutChange(layout2, _onSyncLayout), [_onSyncLayout]);
  const gridClass = _gridClass(locked, isResizable, draggableHandle, className);
  const layout = useMemo(() => positionsToLayout(positions), [positions]);
  return /* @__PURE__ */ React.createElement(
    StyledWidthProvidedGridLayout,
    {
      className: gridClass,
      width,
      breakpoints: BREAKPOINTS,
      cols: columns,
      layouts: { xxl: layout, xl: layout, lg: layout, md: layout, sm: layout, xs: layout },
      rowHeight,
      containerPadding: [0, 0],
      margin: [cellMargin, cellMargin],
      isResizable: !locked && isResizable,
      isDraggable: !locked,
      measureBeforeMount,
      draggableCancel: ".actions",
      onDragStart: removeGaps,
      onDragStop: onLayoutChange,
      onResizeStart: removeGaps,
      onResizeStop: onLayoutChange,
      onLayoutChange: onSyncLayout,
      useCSSTransforms: false,
      draggableHandle: locked ? "" : draggableHandle
    },
    children
  );
};
export default ReactGridContainer;
